import {useState, useEffect} from 'react';
import DOMPurify from 'dompurify';
import { IoClose, IoEyeOutline, IoDownloadOutline, IoWarningOutline } from 'react-icons/io5';
import { MdEdit, MdDelete, MdDriveFileMove, MdShare, MdMoreVert } from 'react-icons/md';
import { BsFileEarmarkPdf, BsFileEarmarkWord, BsFileEarmarkExcel, BsFileEarmarkPpt, BsFileEarmarkImage, BsFileEarmarkPlay, BsFileEarmark } from 'react-icons/bs';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Modal from '@mui/material/Modal';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Zoom from '@mui/material/Zoom';
import * as mime from 'react-native-mime-types';
import fileDownload from 'js-file-download';
import moment from 'moment';
import { useReduxFileStorage } from '@Hooks/reduxFileStorageHook/useReduxFileStorage';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import ProjectFilePermission from '@ProjectFunctionalDisplay/ProjectFilePermission';
import MigrateFileForm from '@ProjectFunctionalDisplay/MigrateFileForm';
import DeleteFileForm from '@ProjectFunctionalDisplay/DeleteFileForm';
import FileService from '@Helpers/URLHelpers/FileService';
import ErrorHandler from '@Root/ErrorHandler';
import './FileTypeFrame.scss';

export default function FileTypeFrame(props) {

    const [cursor, setCursor] = useState('auto');
    const [fileName, setFileName] = useState('');
    const [showFileDeleteForm, setShowFileDeleteForm] = useState(false);
    const [showFileNameInput, setShowFileNameInput] = useState(false);
    const [showInfoOptions, setShowInfoOptions] = useState(false);
    const [anchorInfoMenu, setAnchorInfoMenu] = useState(null);
    const [permission, setPermission] = useState(null);
    const [showFilePermission, setShowFilePermission] = useState(false);
    const [showMigrateFileForm, setShowMigrateFileForm] = useState(false);
    const [fileData, setFileData] = useState(null);
    const [imageUrl, setImageUrl] = useState('');
    const [, setVideoUrl] = useState('');
    const [, setAnchorFileNamePopover] = useState(null);
    const [mimeType, setMimeType] = useState();
    const [showFilePreview, setShowFilePreview] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isCorrupted, setIsCorrupted] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const { downloadFileFromUrl } = useReduxFileStorage();
    const { currentProjectId } = useReduxProject();
    const fileService = new FileService();
    const errorHandler = new ErrorHandler();
    const className = 'fileTypeFrame';

    useEffect(() => {
        parseData();
    }, [])

    const parseData = async() => {
        try {
            setFileName(props?.fileName || 'Unknown File');
            
            // Validate mimeType
            const lookupResult = mime.lookup(props?.fileName);
            
            if (typeof lookupResult !== 'string' || !lookupResult) {
                console.error('Invalid MIME type for file:', props?.fileName);
                setIsCorrupted(true);
                setMimeType('corrupted');
                return;
            }
            
            setMimeType(lookupResult);
            setFileData(props?.fileData);
            setPermission(props.fileData?.permission);

            if(lookupResult.split('/')[0] === 'image') {
                let url = props?.fileData.publicUrl;
                setImageUrl(url);
            }

            if(lookupResult.split('/')[0] === 'video') {
                let url = props?.fileData.publicUrl;
                setVideoUrl(url);
            }
        } catch (error) {
            console.error('Error parsing file data:', error);
            errorHandler.handleError(error);
            setIsCorrupted(true);
            setMimeType('corrupted');
        }
    }

    const handleFileNamePopoverOpen = (event) => {
        setAnchorFileNamePopover(event.currentTarget);
    };

    const handleFileNamePopoverClose = () => {
        setAnchorFileNamePopover(null);
    };

    const dataURLtoFile = (dataurl) => {
        try {
            var arr = dataurl?.split(','),
                mime = arr[0]?.match(/:(.*?);/)[1],
                bstr = atob(arr[1]),
                n = bstr.length,
                u8arr = new Uint8Array(n);
            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }
            return new File([u8arr], { type: mime });
        } catch (error) {
            console.error('Error converting data URL to file:', error);
            throw new Error('Failed to process file data');
        }
    };

    const downloadFile = async() => {
        if (isCorrupted) {
            alert('Cannot download corrupted file');
            return;
        }
        setCursor('wait');
        try {
            downloadFileFromUrl(props.fileData?.publicUrl, props.fileData?.fileName);
        } catch (err) {
            console.error('Error downloading file:', err);
            errorHandler.handleError(err);
            alert('Failed to download file. The file may be corrupted.');
        }
        handleOptionsPopup();
        setCursor('auto');
    }

    const handleOptionsPopup = (event) => {
        if(!showInfoOptions){
            if(!!event) {
                setAnchorInfoMenu(event.currentTarget);
                setShowInfoOptions(true);
            }
        } else {
            setAnchorInfoMenu(null);
            setShowInfoOptions(false);
        }
    }

    const handleRenameFile = async() => {
        toggleShowFileNameInput();
        if(fileName === props.fileName) return;
        setCursor('wait');
        try {
            let name = fileName.split('.');
            if(name.length !== 1) {
                name.length = name.length - 1;
                name = name.join('');
            }else {
                name = name.join('');
            }
            let fileType = props?.fileName.split('.')[1];
            let parentType = props.navigationParentId === 0 ? 'PROJECT' : 'FOLDER';
            let parentId = props.navigationParentId === 0 ? currentProjectId : props.navigationParentId;
            let bodyObj = {
                parentId: parentId,
                parentType: parentType,
                fileId: props.fileId,
                fileName: name + '.' + fileType,
            }
            let response = await fileService.renameFileName(bodyObj);
            if(response.status < 400) {
                setFileName(bodyObj.fileName);
                setShowInfoOptions(false);
                setShowFileNameInput(false);
            }
        } catch(err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const handleFileNameChange = (e)=> {
        let sanitizedFileName = DOMPurify.sanitize(e.target.value);
        setFileName(sanitizedFileName);
    }

    const toggleShowFileNameInput = ()=> {
        setShowFileNameInput(!showFileNameInput);
        handleOptionsPopup();
    }

    const toggleShowFileDeleteForm = ()=> {
        setShowFileDeleteForm(!showFileDeleteForm);
        handleOptionsPopup();
    }

    const toggleShowFilePermission = ()=> {
        setShowFilePermission(!showFilePermission);
        handleOptionsPopup();
    }

    const toggleShowMigrateFileForm = ()=> {
        setShowMigrateFileForm(!showMigrateFileForm);
        handleOptionsPopup();
    }

    const handleFilePreview = () => {
        if (isCorrupted) {
            alert('Cannot preview corrupted file');
            return;
        }
        setShowFilePreview(true);
    };

    const handleClosePreview = () => {
        setShowFilePreview(false);
    };

    const getFileIcon = () => {
        if (isCorrupted) return <IoWarningOutline />;
        if (typeof mimeType !== 'string') return <BsFileEarmark />;
        
        if (mimeType.indexOf('image') > -1) return <BsFileEarmarkImage />;
        if (mimeType.indexOf('video') > -1) return <BsFileEarmarkPlay />;
        if (mimeType.indexOf('pdf') > -1) return <BsFileEarmarkPdf />;
        if (mimeType.indexOf('officedocument.wordprocessingml') > -1) return <BsFileEarmarkWord />;
        if (mimeType.indexOf('officedocument.spreadsheetml') > -1) return <BsFileEarmarkExcel />;
        if (mimeType.indexOf('application/vnd.ms-powerpoint') > -1) return <BsFileEarmarkPpt />;
        return <BsFileEarmark />;
    };

    const getFileTypeLabel = () => {
        if (isCorrupted) return 'Corrupted';
        if (typeof mimeType !== 'string') return 'Unknown';
        
        if (mimeType.indexOf('image') > -1) return 'Image';
        if (mimeType.indexOf('video') > -1) return 'Video';
        if (mimeType.indexOf('pdf') > -1) return 'PDF';
        if (mimeType.indexOf('officedocument.wordprocessingml') > -1) return 'Word';
        if (mimeType.indexOf('officedocument.spreadsheetml') > -1) return 'Excel';
        if (mimeType.indexOf('application/vnd.ms-powerpoint') > -1) return 'PowerPoint';
        const ext = fileName?.split('.').pop()?.toUpperCase();
        return ext || 'File';
    };

    const getFileSize = () => {
        return props?.fileSize || props?.fileData?.fileSize || 'Unknown';
    };

    return (
        <Box 
            className={`${className} ${isCorrupted ? className + '--corrupted' : ''}`}
            style={{cursor: cursor}}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            
            {/* Corrupted File Alert */}
            {isCorrupted && (
                <Alert 
                    severity="error" 
                    className={className + '__corrupted-alert'}
                    icon={<IoWarningOutline />}>
                    File is corrupted or unreadable
                </Alert>
            )}
            
            {/* File Preview/Icon Section */}
            <Box className={className + '__preview'}>
                {isCorrupted ? (
                    <Box className={`${className}__preview__icon ${className}__preview__icon--corrupted`}>
                        <IoWarningOutline />
                    </Box>
                ) : typeof mimeType === 'string' && mimeType.indexOf('image') > -1 ? (
                    imageUrl && !loadError ? (
                        <img
                            className={className + '__preview__image'}
                            src={imageUrl}
                            alt={fileName}
                            onError={() => setLoadError(true)}
                        />
                    ) : (
                        <Box className={className + '__preview__icon'}>
                            {loadError ? <IoWarningOutline /> : getFileIcon()}
                        </Box>
                    )
                ) : (
                    <Box className={className + '__preview__icon'}>
                        {getFileIcon()}
                    </Box>
                )}
                
                {/* Hover Overlay */}
                {isHovered && !isCorrupted && (
                    <Box className={className + '__preview__overlay'}>
                        <Tooltip title="Quick View" arrow placement="top" slots={{ transition: Zoom }}>
                            <IconButton 
                                className={className + '__preview__overlay__action'}
                                onClick={handleFilePreview}
                                size="small">
                                <IoEyeOutline />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Download" arrow placement="top" slots={{ transition: Zoom }}>
                            <IconButton 
                                className={className + '__preview__overlay__action'}
                                onClick={() => downloadFile()}
                                size="small">
                                <IoDownloadOutline />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}

                {/* File Type Badge */}
                <Chip 
                    label={getFileTypeLabel()} 
                    size="small"
                    className={`${className}__preview__badge ${isCorrupted ? className + '__preview__badge--error' : ''}`}
                    color={isCorrupted ? 'error' : 'default'}
                />
            </Box>

            {/* File Info Section */}
            <Box className={className + '__info'}>
                <Box className={className + '__info__header'}>
                    {showFileNameInput ? (
                        <TextField
                            placeholder='Rename file'
                            className={className + '__info__input'}
                            type='text'
                            value={fileName}
                            onChange={handleFileNameChange}
                            onBlur={handleRenameFile}
                            onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                    handleRenameFile();
                                }
                            }}
                            variant='standard'
                            autoFocus
                            fullWidth
                            required
                            disabled={isCorrupted}
                        />
                    ) : (
                        <Tooltip title={fileName} arrow placement="top" slots={{ transition: Zoom }}>
                            <Typography 
                                variant="subtitle2" 
                                className={className + '__info__filename'}
                                onMouseEnter={handleFileNamePopoverOpen} 
                                onMouseLeave={handleFileNamePopoverClose}>
                                {fileName}
                            </Typography>
                        </Tooltip>
                    )}
                    
                    <IconButton 
                        size="small"
                        className={className + '__info__menu-btn'}
                        onClick={handleOptionsPopup}
                        disabled={isCorrupted && !permission?.allowDelete}>
                        <MdMoreVert />
                    </IconButton>
                </Box>

                <Box className={className + '__info__meta'}>
                    <Typography variant="caption" className={className + '__info__meta__item'}>
                        {fileData?.createTimeStamp ? moment(fileData.createTimeStamp).format('MMM DD, YYYY') : 'Unknown date'}
                    </Typography>
                    <Typography variant="caption" className={className + '__info__meta__separator'}>
                        •
                    </Typography>
                    <Typography variant="caption" className={className + '__info__meta__item'}>
                        {getFileSize()}
                    </Typography>
                </Box>
            </Box>

            {/* Options Menu */}
            {showInfoOptions && (
                <Menu
                    open={showInfoOptions}
                    onClose={handleOptionsPopup}
                    anchorEl={anchorInfoMenu}
                    className={className + '__menu'}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
                    <MenuItem 
                        className={className + '__menu__item'}
                        onClick={handleFilePreview}
                        disabled={isCorrupted}>
                        <IoEyeOutline className={className + '__menu__item__icon'} />
                        <Typography>Preview</Typography>
                    </MenuItem>
                    <MenuItem 
                        className={className + '__menu__item'}
                        onClick={() => downloadFile()}
                        disabled={isCorrupted}>
                        <IoDownloadOutline className={className + '__menu__item__icon'} />
                        <Typography>Download</Typography>
                    </MenuItem>
                    <MenuItem 
                        className={className + '__menu__item'}
                        onClick={toggleShowFileNameInput}
                        disabled={!permission?.allowEdit || isCorrupted}>
                        <MdEdit className={className + '__menu__item__icon'} />
                        <Typography>Rename</Typography>
                    </MenuItem>
                    <MenuItem 
                        className={className + '__menu__item'}
                        onClick={toggleShowMigrateFileForm}
                        disabled={true}>
                        <MdDriveFileMove className={className + '__menu__item__icon'} />
                        <Typography>Move</Typography>
                    </MenuItem>
                    <MenuItem 
                        className={className + '__menu__item'}
                        onClick={toggleShowFilePermission}
                        disabled={!permission?.allowAdmin || isCorrupted}>
                        <MdShare className={className + '__menu__item__icon'} />
                        <Typography>Share</Typography>
                    </MenuItem>
                    <Box className={className + '__menu__divider'} />
                    <MenuItem 
                        className={`${className}__menu__item ${className}__menu__item--danger`}
                        onClick={toggleShowFileDeleteForm}
                        disabled={!permission?.allowDelete}>
                        <MdDelete className={className + '__menu__item__icon'} />
                        <Typography>Delete</Typography>
                    </MenuItem>
                </Menu>
            )}

            {/* Dialogs */}
            {showFileDeleteForm && (
                <DeleteFileForm
                    open={showFileDeleteForm}
                    closePopup={toggleShowFileDeleteForm}
                    navigationParentId={props.navigationParentId}
                    fileName={props.fileName}
                    fileId={props.fileId}
                    renderData={props.renderData}
                />
            )}
            
            {showMigrateFileForm && (
                <MigrateFileForm
                    open={showMigrateFileForm}
                    closePopup={toggleShowMigrateFileForm}
                    navigationParentId={props.navigationParentId}
                    fileName={props.fileName}
                    renderData={props.renderData}
                />
            )}
            
            {showFilePermission && (
                <ProjectFilePermission
                    folderId={props.fileId}
                    permission={permission}
                    childType={'projectFiles'}
                    open={showFilePermission}
                    renderData={props.renderData}
                    navigationParentId={props.navigationParentId}
                    closePopup={toggleShowFilePermission}
                />
            )}
            
            {showFilePreview && !isCorrupted && (
                <Modal 
                    open={showFilePreview} 
                    onClose={handleClosePreview}
                    className={className + '__preview-modal'}>
                    <Box className={className + '__preview-modal__content'}>
                        <Box className={className + '__preview-modal__header'}>
                            <Typography variant="h6">{fileName}</Typography>
                            <IconButton onClick={handleClosePreview}>
                                <IoClose />
                            </IconButton>
                        </Box>
                        <Box className={className + '__preview-modal__body'}>
                            {typeof mimeType === 'string' && mimeType.indexOf('image') > -1 ? (
                                imageUrl && !loadError ? (
                                    <img 
                                        src={imageUrl} 
                                        alt={fileName} 
                                        style={{ maxWidth: '100%', maxHeight: '80vh' }}
                                        onError={() => setLoadError(true)}
                                    />
                                ) : (
                                    <Box className={className + '__preview-modal__unavailable'}>
                                        <Box className={className + '__preview-modal__unavailable__icon'}>
                                            <IoWarningOutline />
                                        </Box>
                                        <Typography variant="body1">Failed to load preview</Typography>
                                        <Typography variant="caption">The image could not be loaded</Typography>
                                    </Box>
                                )
                            ) : (
                                <Box className={className + '__preview-modal__unavailable'}>
                                    <Box className={className + '__preview-modal__unavailable__icon'}>
                                        {getFileIcon()}
                                    </Box>
                                    <Typography variant="body1">Preview not available</Typography>
                                    <Typography variant="caption">Download to view this file</Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Modal>
            )}
        </Box>
    );
}
